import { AxiosError, AxiosResponse } from 'axios';
import api from '../api';
import { CONFIG_SERVER, GENDER } from '../constants/common.constant';
import { SORT_ORDER } from '../constants/filter.constant';
import { REPORT_STATUS } from '../constants/report.constant';
import {
  DefaultErrorResponse,
  DefaultResponse,
  Institution,
  Order,
  Procedure,
  VisitedPatient,
} from '../interfaces/index';
import { ESIGN_STATUS } from './esign.api';
import { ACCESS_TYPE } from './share.api';
import { Section } from './templateStructuredReport';

const REPORT_URL = '/ris/reports/v1';
const REPORT_URL_V2 = '/ris/reports/v2';
const REPORT_URL_V3 = '/ris-renovate/v1/reports';

export enum INDICATOR {
  Abnormal,
  Indicator_Normal,
  Unspecified,
  None, //!: Only use for filter
}

interface Metadata {
  his_synced: boolean;
  is_submitted: boolean;
}

interface ShareAccessKey {
  key: string;
  password: string;
  study_instance_uid: string;
  type: ACCESS_TYPE;
  visit_id: string;
}

interface UserInfo {
  address: string;
  birthdate: string;
  created_at: string;
  creator_id: string;
  default_institution_id: null | string;
  department_id: string;
  email: string;
  esign_code: string;
  esign_role: string;
  expired_at: string;
  fullname: string;
  gender: GENDER;
  his_id: string;
  id: string;
  institutions: string[];
  is_super: boolean;
  phone: string;
  roles: string[];
  signature: string;
  status: number;
  title: string;
  updated_at: string;
  username: string;
}

export type Approver = UserInfo;
export type Technician = UserInfo;
export type Submitter = UserInfo;

interface ParamsGetReports {
  his_synced?: boolean;
  institution_code?: string;
  institution_ids?: string[];
  // is_printed?: boolean;
  limit?: number;
  offset?: number;
  order_id?: string;
  report_date_from?: string;
  report_date_to?: string;
  sort_field?: '' | 'user_id' | 'created_at' | 'updated_at' | 'institution_id';
  sort_order?: SORT_ORDER;
  status?: REPORT_STATUS;
  user_ids?: string[];
}

export interface Reports {
  approved_at: string;
  approver: ResponseApprover['data'] | null;
  approver_id: string;
  // conclusion: string;
  // content: string;
  created_at: string;
  created_by: ResponseApprover['data'];
  // film_quantity: number;
  // film_type: number;
  id: string;
  institution: Pick<Institution, 'id' | 'name' | 'code'>;
  institution_id: string;
  // is_printed: boolean;
  is_structure_report: boolean;
  // layout: null | {
  //   content: string;
  //   name: string;
  //   status: LAYOUT_TYPE;
  // };
  // layout_id: null | string;
  metadata: null | Metadata;
  order: Order;
  order_id: string;
  procedure: Procedure & {
    id: string;
    institution_id: string;
    user_id: string | null;
    active: boolean;
    created_at: string;
    updated_at: string;
    institution: null;
    user: null;
    procedure_group: null;
    device_ids: null;
  };
  procedure_id: string;
  session: string;
  share_access_keys: ShareAccessKey[];
  status: REPORT_STATUS;
  structure?: Structure;
  submitted_at: string;
  submitter: Submitter | null;
  submitter_id: string;
  technician: Technician | null;
  technician_id: string;
  updated_at: string;
  updated_by: ResponseApprover['data'];
  updated_by_id: string;
  user_id: string;
  version: number;
  visited_patient: VisitedPatient;
}

export type ResponseGetReports = DefaultResponse<Reports[]>;

export interface Structure {
  id?: string;
  data?: Section[];
}
export const getReports = async (
  params: ParamsGetReports = { limit: 100, offset: 0 }
) => {
  const response = (await api({
    url: REPORT_URL_V2,
    method: 'GET',
    params,
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetReports>;

  return response.data;
};

export interface ResponseApprover extends DefaultResponse {
  data: {
    address: string;
    birthdate: string;
    created_at: string;
    creator_id: string;
    email: string;
    expired_at: string;
    fullname: string;
    gender: number;
    his_id: string;
    id: string;
    institutions: string[];
    is_super: boolean;
    phone: string;
    roles: string[];
    scopes: string[];
    signature: string;
    status: number;
    title: string;
    updated_at: string;
    username: string;
  };
}

interface ParamGetOrderReport {
  accession_no: string;
  patient_id: string;
}

export const getOrderReport = async (params: ParamGetOrderReport) => {
  const response = (await api({
    url: REPORT_URL_V2 + '/order-report',
    method: 'GET',
    params,
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetReports>;

  return response.data;
};

export const getStudyLatestReport = async (study_instance_uid: string) => {
  const response = (await api({
    url: REPORT_URL_V2 + '/study-report/' + study_instance_uid,
    method: 'GET',
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetReports>;

  return response.data;
};

export const getApprover = async (params: { id: string }) => {
  const { id } = params;

  if (!id) return null;

  const response = (await api({
    url: `${REPORT_URL}/approver/${id}`,
    method: 'GET',
    timeout: 30_000,
  })) as AxiosResponse<ResponseApprover>;

  return response.data;
};

export interface EsignOrder {
  created_at: string;
  id: string;
  order_id: string;
  receiver: string;
  report_id: string;
  report_version_id: string;
  revoke_id: null;
  sender: string;
  status: ESIGN_STATUS;
  updated_at: string;
}

export interface LatestReportByOrder {
  approved_at: string;
  approver_id: string;
  created_at: string;
  esign_order: null | EsignOrder;
  id: string;
  institution_id: string;
  is_structure_report: boolean;
  metadata: Metadata;
  order_id: string;
  session: string;
  share_access_keys: ShareAccessKey[];
  status: number;
  structure: Structure;
  submitted_at: string;
  submitter_id: string;
  technician_id: string;
  updated_at: string;
  updated_by_id: string;
  user_id: string;
  version: number;
  report_version: ReportVersion;
}

export interface ReportVersion {
  action_at: string;
  approved_at: null | string;
  approver: null | Approver;
  approver_id: null | string;
  assign_to: null | string;
  conclusion: string;
  content: string;
  created_at: string;
  created_by_id: string;
  created_by_name: string;
  film_quantity: null | number;
  film_type: number;
  id: string;
  indicator: null | INDICATOR;
  is_structure_report: boolean;
  layout_id: string;
  report_id: string;
  status: number;
  structure: Structure;
  submitted_at: string;
  submitter: null | Submitter;
  submitter_id: string;
  technician: null | Technician;
  technician_id: null | string;
  version: number;
}

export type LatestReportByOrderResponse = DefaultResponse<LatestReportByOrder>;

export const getLatestReportByOrder = async (order_id: string) => {
  const response = (await api({
    url: `${REPORT_URL_V2}/latest-version-detail-by-order/${order_id}`,
    method: 'GET',
    timeout: 30_000,
  })) as AxiosResponse<LatestReportByOrderResponse>;

  return response.data;
};

export interface ReportByVersionId {
  action_at: string;
  approved_at: string;
  approver: Approver;
  approver_id: string;
  assign_to: string;
  conclusion: string;
  content: string;
  created_at: string;
  created_by_id: string;
  esign_order: null | EsignOrder;
  film_quantity: null;
  film_type: number;
  id: string;
  indicator: null;
  is_structure_report: boolean;
  layout: ReportLayout;
  layout_id: string;
  report_id: string;
  status: REPORT_STATUS;
  structure: Structure;
  submitted_at: string;
  submitter: Submitter;
  submitter_id: string;
  technician: Technician | null;
  technician_id: null;
  version: number;
}

export type ResponseReportByVersionId = DefaultResponse<ReportByVersionId>;

export const getReportByVersionId = async (report_version_id: string) => {
  const response = (await api({
    url: `${REPORT_URL_V2}/versions/${report_version_id}`,
    method: 'GET',
    timeout: 30_000,
  })) as AxiosResponse<ResponseReportByVersionId>;

  return response.data;
};

export interface ReportLayout {
  active: boolean;
  content: string;
  created_at: string;
  id: string;
  institution_id: string;
  name: string;
  status: number;
  updated_at: string;
  user_id: string;
}

export interface ReportVersions {
  action_at: string;
  created_by_id: string;
  created_by_name: string;
  submitted_by_name: string;
  submitted_by_id: string;
  approved_by_name: string;
  approved_by_id: string;
  id: string;
  status: REPORT_STATUS;
  version: number;
}

export type ReportVersionsResponse = DefaultResponse<ReportVersions[]>;

export const getReportVersions = async (report_id: string) => {
  const response = (await api({
    url: `${REPORT_URL_V2}/${report_id}/versions`,
    method: 'GET',
    timeout: 30_000,
  })) as AxiosResponse<ReportVersionsResponse>;

  return response.data;
};

export interface ParamsCreateReport {
  institution_id: string;
  is_structure_report?: boolean;
  order_assign_id?: string;
  order_id: string;
}

export interface ResponseCreateReport {
  approved_at: string;
  approver_id: string;
  assign_to: null | string;
  conclusion: string;
  content: string;
  created_at: string;
  film_quantity: null;
  film_type: null;
  id: string;
  indicator: null | INDICATOR;
  institution: null;
  institution_id: string;
  is_printed: boolean;
  is_structure_report: boolean;
  layout_id: null;
  metadata: Metadata;
  order_id: string;
  session: string;
  share_access_keys: ShareAccessKey[] | null;
  status: REPORT_STATUS.READING;
  structure: null;
  submitted_at: string;
  submitter_id: string;
  technician_id: string;
  updated_at: string;
  user: null;
  user_id: string;
}

export const createReport = async (data: ParamsCreateReport) => {
  const response = (await api({
    url: REPORT_URL_V3,
    method: 'POST',
    data,
  })) as AxiosResponse<ResponseCreateReport>;

  return response.data;
};

export interface ParamsCreateHISReport {
  approved_at?: string;
  approver_his_id?: string;
  conclusion?: string;
  content?: string;
  created_at?: string;
  his_vendor?: string;
  institution_id: string;
  message_type?: string;
  order_doctor_his_id?: string;
  order_id: string;
  processing_id?: string;
  submitted_at?: string;
  submitter_his_id?: string;
  technician_his_id?: string;
}

export const createHISReport = async (data: ParamsCreateHISReport) => {
  const response = (await api({
    url: REPORT_URL_V2 + '/hisconnect-synchronization',
    method: 'POST',
    data,
  })) as AxiosResponse<ResponseCreateReport>;

  return response.data;
};

export interface ParamsUpdateApproveReport {
  assign_to: string;
  conclusion: string;
  content: string;
  indicator: INDICATOR;
  layout_id: string;
  order_assign_id?: string;
  structure: Structure;
  technician_id: string;
}
export const updateApproveReport = async (
  id: string,
  data: ParamsUpdateApproveReport
) => {
  const response = (await api({
    url: `${REPORT_URL_V3}/${id}/approval`,
    method: 'PATCH',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ParamsUpdateEditReport {
  order_assign_id?: string;
}

export type UpdateEditReportErrorResponse = AxiosError<
  DefaultErrorResponse<'EMSG_0003'>
>;

export interface ParamEditReport {
  order_assign_id: string;
}

export const updateEditReport = async (id: string, data: ParamEditReport) => {
  try {
    const response = (await api({
      url: `${REPORT_URL_V3}/${id}/edition`,
      method: 'PATCH',
      data,
    })) as AxiosResponse<any>;

    return response.data;
  } catch (error) {
    throw error as UpdateEditReportErrorResponse;
  }
};

export interface ParamsUpdateSubmitReport {
  // order_assign_id?: string;
  assign_to: string;
  conclusion: string;
  content: string;
  film_quantity: number;
  film_type: number;
  indicator: INDICATOR;
  is_structure_report?: boolean;
  layout_id: string;
  structure?: Structure;
  technician_id: string;
}
export const updateSubmitReport = async (
  id: string,
  data: ParamsUpdateSubmitReport
) => {
  const response = (await api({
    url: `${REPORT_URL_V3}/${id}/submission`,
    method: 'PATCH',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

export interface ParamsUpdateCancelReport {
  order_assign_id?: string;
  version_id: string;
}
export const updateCancelReport = async (
  id: string,
  data: ParamsUpdateCancelReport
) => {
  try {
    const response = (await api({
      url: `${REPORT_URL_V3}/${id}/cancellation`,
      method: 'DELETE',
      data,
    })) as AxiosResponse<any>;

    return response.data;
  } catch (error) {
    throw error as UpdateEditReportErrorResponse;
  }
};

export interface ParamMarkReportSyncStatus {
  his_synced: boolean;
  his_vendor: string;
  message_type: string;
  processing_id: string;
}
export const markReportSyncStatus = async (
  id: string,
  data: ParamMarkReportSyncStatus
) => {
  const response = (await api({
    url: `${REPORT_URL_V2}/hisconnect-synchronization/mark-sync-status/${id}`,
    method: 'PUT',
    data,
  })) as AxiosResponse<any>;

  return response.data;
};

interface ParamsGetTestInformation {
  patient_id?: string;
  institution_id?: string;
}

export interface TestInformation {
  date_id: string;
  categories: {
    category_id: string;
    category_name: string;
    category_name_e: string;
    profiles: {
      profile_id: string;
      profile_name: string;
      data: {
        test_code: string;
        test_name: string;
        test_name_e: string;
        result: string;
        lower_limit: string;
        higher_limit: string;
        normal_range: string;
        unit: string;
        updated_at: string;
      }[];
    }[];
  }[];
}

export type TestInformationResponse = DefaultResponse<TestInformation[]>;

export const getTestInformation = async (params: ParamsGetTestInformation) => {
  const response = (await api({
    url: '/ris-renovate/v1/reports/clinical-information/test',
    method: 'GET',
    params,
    timeout: 30_000,
  })) as AxiosResponse<TestInformationResponse>;

  return response.data;
};
